import React, { useEffect, useState, createContext, useContext } from 'react';
export type OrderStatus = 'new' | 'preparing' | 'ready' | 'completed' | 'cancelled';
export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'refunded';
export type OrderMethod = 'table' | 'takeout' | 'dineAhead' | 'delivery';
export type PaymentMethod = 'card' | 'cash' | 'telebirr' | 'cbe-birr' | 'bank-transfer';
export type OrderItem = {
  id: string;
  name: string;
  quantity: number;
  unitPrice: number;
  variation?: string;
  modifiers?: {
    name: string;
    price: number;
  }[];
  notes?: string;
  ready?: boolean;
  // Set when the kitchen marks the item ready. Lets the waiter terminal
  // surface it in the "Ready for pickup" lane and gives us an audit trail
  // for kitchen→table lag.
  readyAt?: number;
  stationId?: string;
  // For waiter-originated tab items: tracks the table-handoff step. Captured
  // when the waiter marks the item delivered on the tab UI; surfaced on the
  // cashier-side bill so disputes have an audit trail.
  deliveredAt?: number;
  deliveredBy?: string;
  // Flagged true when this item is a comp remake of a previously delivered
  // dish (quality issue). Kitchen ticket renders a "REMAKE" badge with the
  // reason; bill calculation skips comp items so the customer isn't charged.
  complimentary?: boolean;
  remakeReason?: string;
  // Points back to the original delivered item this remake/reorder is for.
  originalItemId?: string;
};
// Heuristic routing: map an item name to a canonical station id.
// Used as a fallback when an item doesn't carry an explicit stationId.
export function inferStationId(itemName: string): string {
  const n = itemName.toLowerCase();
  if (/(coffee|latte|macchiato|cappuccino|espresso|americano|tea|juice|smoothie|water|soda|cola|wine|beer|cocktail|mojito|martini|ambo)/.test(n)) {
    return 'bar';
  }
  if (/(salad|wrap)/.test(n)) return 'cold';
  if (/(tiramisu|croissant|cake|cookie|brownie|ice cream|gelato|pastry|donut|pie)/.test(n)) {
    return 'dessert';
  }
  return 'food';
}
export function stationForItem(item: OrderItem): string {
  return item.stationId || inferStationId(item.name);
}
// Default branch a seed/legacy order belongs to when no branchId is set.
// Keeps the multi-branch filter logic safe for orders created before
// branchId was a tracked field.
export const DEFAULT_BRANCH_ID = 'b_main';
export const orderBranchId = (o: {
  branchId?: string;
}) => o.branchId || DEFAULT_BRANCH_ID;
export type Order = {
  id: string;
  customerName: string;
  customerPhone: string;
  // Branch this order belongs to. Optional so historical seed orders without
  // a branchId still validate; readers default to 'b_main' via orderBranchId().
  branchId?: string;
  outletId?: string;
  method: 'takeout' | 'delivery' | 'dineAhead' | 'table';
  tableNumber?: number;
  deliveryAddress?: string;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  // Optional until payment is collected. Tab orders start with no method
  // and the waiter (or cashier) records it at payment time.
  paymentMethod?: PaymentMethod;
  paymentVerifiedAt?: number;
  bankTransferRef?: string;
  selectedBankAccountId?: string;
  commissionRate?: number;
  commissionAmount?: number;
  commissionStatus?: 'accrued' | 'settled';
  items: OrderItem[];
  subtotal: number;
  packagingFee: number;
  serviceFee: number;
  discountCode?: string;
  discountAmount?: number;
  total: number;
  placedAt: number;
  estimatedMinutes: number;
  customerNotes?: string;
  tabId?: string;
  isOpenTab?: boolean;
  splitOf?: {
    groupId: string;
    index: number;
    total: number;
  };
  cancellation?: {
    reason: string;
    wasteValue: number;
    cancelledAt: number;
  };
  lastEditedAt?: number;
  lastEditedBy?: string;
  answers?: {
    questionId: string;
    question: string;
    answer: string | string[];
  }[];
};
type Ctx = {
  orders: Order[];
  setStatus: (id: string, status: OrderStatus, cancellation?: Order['cancellation']) => void;
  setPaymentStatus: (id: string, status: PaymentStatus) => void;
  markPrinted: (id: string) => void;
  setItemReady: (orderId: string, itemId: string, ready: boolean) => void;
  setAllItemsReady: (orderId: string) => void;
  recentlyVerifiedId: string | null;
  addOrder: (order: Order) => void;
  updateOrder: (id: string, patch: Partial<Order>) => void;
  deleteOrder: (id: string) => void;
};
const OrdersContext = createContext<Ctx | null>(null);
function calcItemTotal(item: OrderItem): number {
  const modTotal = (item.modifiers || []).reduce((s, m) => s + m.price, 0);
  return (item.unitPrice + modTotal) * item.quantity;
}
function makeOrder(id: string, partial: Partial<Order> & Pick<Order, 'customerName' | 'method' | 'items' | 'placedAt'>): Order {
  const subtotal = partial.items.reduce((s, i) => s + calcItemTotal(i), 0);
  const packagingFee = partial.method === 'takeout' || partial.method === 'delivery' ? 0.75 : 0;
  const serviceFee = +(subtotal * 0.05).toFixed(2);
  const total = +(subtotal + packagingFee + serviceFee).toFixed(2);
  // Default commission logic for seed orders
  let commissionRate = 0;
  let commissionStatus: 'accrued' | 'settled' | undefined = undefined;
  const pm = partial.paymentMethod || 'card';
  if (pm === 'telebirr' || pm === 'cbe-birr' || pm === 'bank-transfer') {
    commissionRate = 0.75;
  } else if (pm === 'card') {
    commissionRate = 2.9;
  }
  const commissionAmount = +(total * (commissionRate / 100)).toFixed(2);
  if (partial.paymentStatus === 'paid') {
    commissionStatus = pm === 'bank-transfer' ? 'accrued' : 'settled';
  }
  return {
    id,
    customerPhone: '+251911000000',
    status: 'new',
    paymentStatus: 'pending',
    paymentMethod: 'card',
    estimatedMinutes: 20,
    ...partial,
    subtotal,
    packagingFee,
    serviceFee,
    total,
    commissionRate: partial.commissionRate ?? commissionRate,
    commissionAmount: partial.commissionAmount ?? commissionAmount,
    commissionStatus: partial.commissionStatus ?? commissionStatus
  };
}
const now = Date.now();
const minutes = (m: number) => now - m * 60 * 1000;
const DEMO_BANK_TRANSFER_ID = 'ORD-7731';
const demoBankTransferOrder: Order = makeOrder(DEMO_BANK_TRANSFER_ID, {
  customerName: 'Selam Tesfaye',
  customerPhone: '+251911223344',
  method: 'takeout',
  status: 'new',
  paymentStatus: 'pending',
  paymentMethod: 'bank-transfer',
  selectedBankAccountId: 'ba_cbe',
  bankTransferRef: 'FT24159GH7P2',
  placedAt: now - 1 * 60 * 1000,
  items: [{
    id: 'i_burger',
    name: 'Beef Burger Deluxe',
    quantity: 1,
    unitPrice: 24,
    modifiers: [{
      name: 'Extra cheese',
      price: 1.5
    }, {
      name: 'Avocado',
      price: 2
    }]
  }, {
    id: 'i_juice',
    name: 'Fresh Juice',
    quantity: 1,
    unitPrice: 6
  }]
});
const seed: Order[] = [demoBankTransferOrder, makeOrder('ORD-1042', {
  customerName: 'Selam Tadesse',
  method: 'table',
  tableNumber: 7,
  status: 'new',
  paymentStatus: 'pending',
  paymentMethod: 'telebirr',
  placedAt: minutes(0.05),
  items: [{
    id: 'a',
    name: 'American Breakfast',
    quantity: 2,
    unitPrice: 15,
    variation: 'Hungry',
    modifiers: [{
      name: 'Extra eggs',
      price: 2
    }]
  }, {
    id: 'b',
    name: 'Macchiato',
    quantity: 2,
    unitPrice: 4
  }],
  customerNotes: 'No bacon please'
}), makeOrder('ORD-1041', {
  customerName: 'Yonas Bekele',
  method: 'takeout',
  status: 'new',
  paymentStatus: 'paid',
  paymentMethod: 'card',
  placedAt: minutes(2),
  paymentVerifiedAt: minutes(1.9),
  items: [{
    id: 'a',
    name: 'Salmon Wrap',
    quantity: 1,
    unitPrice: 18,
    modifiers: [{
      name: 'Side salad',
      price: 3
    }]
  }, {
    id: 'b',
    name: 'Fresh Juice',
    quantity: 1,
    unitPrice: 6
  }]
}), makeOrder('ORD-1043', {
  customerName: 'Kalkidan Mulu',
  customerPhone: '+251911223344',
  method: 'delivery',
  status: 'preparing',
  paymentStatus: 'paid',
  paymentMethod: 'telebirr',
  placedAt: minutes(5),
  paymentVerifiedAt: minutes(5),
  estimatedMinutes: 35,
  deliveryAddress: 'Bole Edna Mall, Apt 3B, near the back gate',
  items: [{
    id: 'a',
    name: 'Beef Burger Deluxe',
    quantity: 1,
    unitPrice: 24,
    modifiers: [{
      name: 'Extra cheese',
      price: 1.5
    }]
  }, {
    id: 'b',
    name: 'French fries',
    quantity: 1,
    unitPrice: 4
  }, {
    id: 'c',
    name: 'Iced Latte',
    quantity: 1,
    unitPrice: 5
  }],
  customerNotes: 'Please call when at the gate'
}), makeOrder('ORD-1040', {
  customerName: 'Hanna Girma',
  method: 'dineAhead',
  status: 'preparing',
  paymentStatus: 'paid',
  paymentMethod: 'telebirr',
  placedAt: minutes(8),
  paymentVerifiedAt: minutes(8),
  estimatedMinutes: 15,
  items: [{
    id: 'a',
    name: 'Avocado & Salmon Toast',
    quantity: 2,
    unitPrice: 16
  }, {
    id: 'b',
    name: 'Ethiopian Coffee',
    quantity: 2,
    unitPrice: 5
  }, {
    id: 'c',
    name: 'Sparkling Water',
    quantity: 1,
    unitPrice: 3
  }],
  customerNotes: 'Arriving at 8:00pm'
}), makeOrder('ORD-1039', {
  customerName: 'Daniel Mekonnen',
  method: 'table',
  tableNumber: 12,
  status: 'preparing',
  paymentStatus: 'pending',
  paymentMethod: 'cash',
  placedAt: minutes(11),
  items: [{
    id: 'a',
    name: 'Beef Burger Deluxe',
    quantity: 1,
    unitPrice: 24,
    modifiers: [{
      name: 'Extra cheese',
      price: 1.5
    }, {
      name: 'French fries',
      price: 4
    }]
  }, {
    id: 'b',
    name: 'Iced Latte',
    quantity: 1,
    unitPrice: 5
  }]
}), makeOrder('ORD-1038', {
  customerName: 'Meron Alemu',
  method: 'takeout',
  status: 'ready',
  paymentStatus: 'paid',
  paymentMethod: 'cbe-birr',
  placedAt: minutes(18),
  paymentVerifiedAt: minutes(18),
  estimatedMinutes: 15,
  items: [{
    id: 'a',
    name: 'Avocado Toast',
    quantity: 1,
    unitPrice: 11,
    variation: 'Full',
    modifiers: [{
      name: 'Poached egg',
      price: 2
    }]
  }, {
    id: 'b',
    name: 'Macchiato',
    quantity: 1,
    unitPrice: 4
  }]
}), makeOrder('ORD-1037', {
  customerName: 'Abel Tesfaye',
  method: 'table',
  tableNumber: 3,
  status: 'completed',
  paymentStatus: 'paid',
  paymentMethod: 'card',
  placedAt: minutes(45),
  paymentVerifiedAt: minutes(44),
  items: [{
    id: 'a',
    name: 'Pasta Carbonara',
    quantity: 2,
    unitPrice: 19
  }, {
    id: 'b',
    name: 'Ethiopian Coffee',
    quantity: 2,
    unitPrice: 5
  }]
}), makeOrder('ORD-1036', {
  customerName: 'Liya Asfaw',
  method: 'dineAhead',
  status: 'completed',
  paymentStatus: 'paid',
  paymentMethod: 'telebirr',
  placedAt: minutes(72),
  paymentVerifiedAt: minutes(72),
  items: [{
    id: 'a',
    name: 'Salmon Wrap',
    quantity: 1,
    unitPrice: 18
  }, {
    id: 'b',
    name: 'Fresh Juice',
    quantity: 1,
    unitPrice: 6
  }]
}), makeOrder('ORD-1035', {
  customerName: 'Tigist Hailu',
  method: 'takeout',
  status: 'cancelled',
  paymentStatus: 'refunded',
  paymentMethod: 'card',
  placedAt: minutes(95),
  items: [{
    id: 'a',
    name: 'American Breakfast',
    quantity: 1,
    unitPrice: 15
  }]
})];
// Persist orders so a checkout placed in the customer tab (/menu opens in a new
// tab) reaches the cashier's /orders tab — and survives refresh. Mirrors the
// cart's localStorage approach, plus a cross-tab 'storage' listener for live sync.
const ORDERS_STORAGE_KEY = 'mp_orders';
function loadInitialOrders(): Order[] {
  if (typeof window === 'undefined') return seed;
  try {
    const raw = window.localStorage.getItem(ORDERS_STORAGE_KEY);
    if (!raw) return seed;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : seed;
  } catch {
    return seed;
  }
}
export function OrdersProvider({
  children
}: {
  children: ReactNode;
}) {
  const [orders, setOrders] = useState<Order[]>(loadInitialOrders);
  const [recentlyVerifiedId, setRecentlyVerifiedId] = useState<string | null>(null);
  // One-time injection of the demo bank-transfer order for stores that were
  // already persisted before this seed existed. Gated by a flag so it won't
  // come back if the user deletes it.
  useEffect(() => {
    try {
      const alreadySeeded = window.localStorage.getItem('mp_demo_bt_seeded_v1');
      if (alreadySeeded) return;
      window.localStorage.setItem('mp_demo_bt_seeded_v1', '1');
      setOrders(prev => prev.some(o => o.id === DEMO_BANK_TRANSFER_ID) ? prev : [demoBankTransferOrder, ...prev]);
    } catch {
      /* ignore */
    }
  }, []);
  // Persist on every change so other tabs (and refreshes) see the same orders.
  useEffect(() => {
    try {
      window.localStorage.setItem(ORDERS_STORAGE_KEY, JSON.stringify(orders));
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [orders]);
  // Live cross-tab sync: when another tab writes a new order, pick it up here
  // so the cashier sees customer checkouts appear without a manual refresh.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== ORDERS_STORAGE_KEY || e.newValue == null) return;
      try {
        const parsed = JSON.parse(e.newValue);
        if (Array.isArray(parsed)) setOrders(parsed);
      } catch {
        /* ignore malformed payloads */
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  // Auto-payment verification: simulate webhook by flipping pending → paid
  // for orders that use online methods (telebirr, card, cbe-birr) after ~6 seconds
  useEffect(() => {
    const id = setInterval(() => {
      const t = Date.now();
      setOrders(prev => {
        let verifiedId: string | null = null;
        const next = prev.map(o => {
          // Simulated webhook: only auto-verify orders that were explicitly
          // placed with an online method (Telebirr, Card, CBE Birr). Orders
          // with no method set yet (e.g. waiter handoffs awaiting cashier
          // verification) MUST NOT be auto-verified — the cashier owns that
          // decision. Treating "not cash" as a green light incorrectly caught
          // undefined-method orders too.
          const isOnlinePayment = o.paymentMethod === 'telebirr' || o.paymentMethod === 'card' || o.paymentMethod === 'cbe-birr';
          if (o.paymentStatus === 'pending' && isOnlinePayment && t - o.placedAt > 6000) {
            verifiedId = o.id;
            return {
              ...o,
              paymentStatus: 'paid' as PaymentStatus,
              paymentVerifiedAt: t
            };
          }
          return o;
        });
        if (verifiedId) {
          setRecentlyVerifiedId(verifiedId);
          setTimeout(() => setRecentlyVerifiedId(cur => cur === verifiedId ? null : cur), 3000);
        }
        return next;
      });
    }, 2000);
    return () => clearInterval(id);
  }, []);
  const setStatus = (id: string, status: OrderStatus, cancellation?: Order['cancellation']) => {
    setOrders(prev => prev.map(o => o.id === id ? {
      ...o,
      status,
      cancellation: cancellation || o.cancellation,
      lastEditedAt: Date.now(),
      lastEditedBy: 'Cashier'
    } : o));
  };
  const markPrinted = (id: string) => {
    setOrders(prev => prev.map(o => o.id === id ? {
      ...o,
      lastEditedAt: Date.now(),
      lastEditedBy: 'System'
    } : o));
  };
  const setItemReady = (orderId: string, itemId: string, ready: boolean) => {
    const now = Date.now();
    setOrders(prev => prev.map(o => {
      if (o.id !== orderId) return o;
      const items = o.items.map(it => it.id === itemId ? {
        ...it,
        ready,
        readyAt: ready ? now : undefined
      } : it);
      return {
        ...o,
        items,
        lastEditedAt: now,
        lastEditedBy: 'Kitchen'
      };
    }));
  };
  const setAllItemsReady = (orderId: string) => {
    const now = Date.now();
    setOrders(prev => prev.map(o => o.id === orderId ? {
      ...o,
      items: o.items.map(it => ({
        ...it,
        ready: true,
        readyAt: it.readyAt ?? now
      })),
      lastEditedAt: now,
      lastEditedBy: 'Kitchen'
    } : o));
  };
  const setPaymentStatus = (id: string, status: PaymentStatus) => {
    const t = Date.now();
    setOrders(prev => prev.map(o => {
      if (o.id !== id) return o;
      // Prevent marking open waiter tabs as paid. The tab must be closed
      // by the waiter first, which flips isOpenTab to false.
      if (o.isOpenTab && status === 'paid') {
        return o;
      }
      let commissionStatus = o.commissionStatus;
      if (status === 'paid') {
        commissionStatus = o.paymentMethod === 'bank-transfer' ? 'accrued' : 'settled';
      }
      return {
        ...o,
        paymentStatus: status,
        paymentVerifiedAt: status === 'paid' ? t : undefined,
        commissionStatus,
        lastEditedAt: t,
        lastEditedBy: 'Cashier'
      };
    }));
  };
  const addOrder = (order: Order) => {
    setOrders(prev => [order, ...prev]);
  };
  const updateOrder = (id: string, patch: Partial<Order>) => {
    setOrders(prev => prev.map(o => o.id === id ? {
      ...o,
      ...patch,
      lastEditedAt: Date.now(),
      lastEditedBy: 'System'
    } : o));
  };
  return <OrdersContext.Provider value={{
    orders,
    setStatus,
    setPaymentStatus,
    markPrinted,
    setItemReady,
    setAllItemsReady,
    recentlyVerifiedId,
    addOrder,
    updateOrder
  }} data-id="element-2520">
      {children}
    </OrdersContext.Provider>;
}
export function useOrders() {
  const ctx = useContext(OrdersContext);
  if (!ctx) throw new Error('useOrders must be used within OrdersProvider');
  return ctx;
}
export function timeSincePlaced(placedAt: number): string {
  const seconds = Math.max(0, Math.floor((Date.now() - placedAt) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m ago`;
}
export const statusConfig: Record<OrderStatus, {
  label: string;
  color: string;
  bg: string;
  ring: string;
}> = {
  new: {
    label: 'New',
    color: 'text-blue-700',
    bg: 'bg-blue-100',
    ring: 'ring-blue-400'
  },
  preparing: {
    label: 'Preparing',
    color: 'text-amber-700',
    bg: 'bg-amber-100',
    ring: 'ring-amber-400'
  },
  ready: {
    label: 'Ready',
    color: 'text-emerald-700',
    bg: 'bg-emerald-100',
    ring: 'ring-emerald-400'
  },
  completed: {
    label: 'Completed',
    color: 'text-gray-600',
    bg: 'bg-gray-100',
    ring: 'ring-gray-300'
  },
  cancelled: {
    label: 'Cancelled',
    color: 'text-red-700',
    bg: 'bg-red-100',
    ring: 'ring-red-300'
  }
};
export const paymentConfig: Record<PaymentStatus, {
  label: string;
  color: string;
  bg: string;
}> = {
  pending: {
    label: 'Pending',
    color: 'text-amber-700',
    bg: 'bg-amber-50 border-amber-200'
  },
  paid: {
    label: 'Paid',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50 border-emerald-200'
  },
  failed: {
    label: 'Failed',
    color: 'text-red-700',
    bg: 'bg-red-50 border-red-200'
  },
  refunded: {
    label: 'Refunded',
    color: 'text-gray-700',
    bg: 'bg-gray-100 border-gray-200'
  }
};
export const methodConfig: Record<OrderMethod, {
  label: string;
  short: string;
  color: string;
  bg: string;
}> = {
  table: {
    label: 'Table-side (QR)',
    short: 'Table',
    color: 'text-purple-700',
    bg: 'bg-purple-50 border-purple-200'
  },
  takeout: {
    label: 'Takeout',
    short: 'Takeout',
    color: 'text-orange-700',
    bg: 'bg-orange-50 border-orange-200'
  },
  dineAhead: {
    label: 'Dine-in (ahead)',
    short: 'Dine-ahead',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50 border-emerald-200'
  },
  delivery: {
    label: 'Delivery',
    short: 'Delivery',
    color: 'text-blue-700',
    bg: 'bg-blue-50 border-blue-200'
  }
};
export const paymentMethodLabel: Record<PaymentMethod, string> = {
  card: 'Card',
  cash: 'Cash',
  telebirr: 'Telebirr',
  'cbe-birr': 'CBE Birr',
  'bank-transfer': 'Bank Transfer'
};